import * as Cesium from "cesium";
import modifyMap from "@/cesium/modifyMap";

/** 默认相机视角（WGS84，与 Cesium camera 一致） */
export const MAP_CENTER = {
  lng: 120.632812,
  lat: 31.313167,
  height: 17964,
  heading: 360,
  pitch: -50,
};

Cesium.Ion.defaultAccessToken =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiIwMDE2ZTg5Mi1mNDJmLTQ5NWItOGMwZC01YzUyZmE4ZDY2NzMiLCJpZCI6MTM1NTcxLCJzdWIiOiJoYW53dWppMzIxIiwiaXNzIjoiaHR0cHM6Ly9hcGkuY2VzaXVtLmNvbSIsImF1ZCI6Inpob3VxbSIsImlhdCI6MTc4NDUxMzI1MX0.7LaUZiXqL_Q_ycOh04LGnk0d62aqI-M0kghlDI61TTk";

const VIEWER_KEY = "__XC_SHARED_VIEWER__";

function getShared() {
  return typeof window !== "undefined" ? window[VIEWER_KEY] : null;
}

function setShared(viewer) {
  if (typeof window !== "undefined") window[VIEWER_KEY] = viewer;
}

function viewerCanvasInContainer(viewer, container) {
  try {
    const canvas = viewer?.scene?.canvas;
    return !!(container && canvas && container.contains(canvas));
  } catch (_) {
    return false;
  }
}

/**
 * 创建共享 Viewer（全应用仅一次）
 */
export function createSharedViewer(containerId = "cesiumContainer") {
  const container = document.getElementById(containerId);
  const existing = getShared();

  if (existing) {
    const alive = !existing.isDestroyed?.();
    if (alive && viewerCanvasInContainer(existing, container)) {
      return existing;
    }
    destroySharedViewer();
  }

  window.CESIUM_BASE_URL = process.env.BASE_URL;

  if (container) container.innerHTML = "";

  Cesium.Camera.DEFAULT_VIEW_RECTANGLE = Cesium.Rectangle.fromDegrees(
    118.5,
    30.5,
    122.5,
    32.5
  );

  const viewer = new Cesium.Viewer(containerId, {
    infoBox: false,
    selectionIndicator: false,
    animation: false,
    timeline: false,
    // 临时用地形椭球，排除 Ion 地形瓦片并发销毁问题
    terrainProvider: new Cesium.EllipsoidTerrainProvider(),
    // 底图由 modifyMap 异步挂载（反色滤镜包装），避免默认层与私有着色器冲突
    baseLayer: false,
    shouldAnimate: true,
    baseLayerPicker: false,
    geocoder: false,
    homeButton: false,
    sceneModePicker: false,
    navigationHelpButton: false,
    fullscreenButton: false,
  });

  viewer.cesiumWidget.creditContainer.style.display = "none";
  viewer.cesiumWidget.showErrorPanel = false;
  // sun 已关闭时不要开 lighting，否则底图几乎全黑
  viewer.scene.globe.enableLighting = false;
  viewer.scene.skyBox.show = false;
  viewer.scene.sun.show = false;
  viewer.scene.moon.show = false;
  viewer.scene.skyAtmosphere.show = false;
  viewer.scene.backgroundColor = Cesium.Color.BLACK;
  viewer.scene.globe.baseColor = Cesium.Color.fromCssColorString("#0a1520");
  viewer.scene.postProcessStages.fxaa.enabled = true;
  // 避免贴地矢量在近景被 globe 深度挡住
  viewer.scene.globe.depthTestAgainstTerrain = false;
  modifyMap(viewer);

  viewer.camera.setView({
    destination: Cesium.Cartesian3.fromDegrees(
      MAP_CENTER.lng,
      MAP_CENTER.lat,
      MAP_CENTER.height
    ),
    orientation: {
      heading: Cesium.Math.toRadians(MAP_CENTER.heading),
      pitch: Cesium.Math.toRadians(MAP_CENTER.pitch),
      roll: 0,
    },
  });

  setShared(viewer);
  return viewer;
}

export function destroySharedViewer() {
  const sharedViewer = getShared();
  if (sharedViewer && !sharedViewer.isDestroyed()) {
    try {
      sharedViewer.useDefaultRenderLoop = false;
      sharedViewer.destroy();
    } catch (_) {
      /* ignore */
    }
  }
  setShared(null);
}

/** 读取当前相机视角（WGS84 度数） */
export function captureCameraView(viewer) {
  if (!viewer || viewer.isDestroyed()) return null;
  const cam = viewer.camera;
  const carto = Cesium.Cartographic.fromCartesian(cam.positionWC);
  return {
    lng: Number(Cesium.Math.toDegrees(carto.longitude).toFixed(6)),
    lat: Number(Cesium.Math.toDegrees(carto.latitude).toFixed(6)),
    height: Math.round(carto.height),
    heading: Number(Cesium.Math.toDegrees(cam.heading).toFixed(2)),
    pitch: Number(Cesium.Math.toDegrees(cam.pitch).toFixed(2)),
    roll: Number(Cesium.Math.toDegrees(cam.roll).toFixed(2)),
  };
}

/** 飞到经纬度并让目标点落在视野中心（height 为相机到目标的距离） */
export function flyToLonLat(viewer, lng, lat, height = 2500) {
  if (!viewer || viewer.isDestroyed() || lng == null || lat == null) return;
  const target = Cesium.Cartesian3.fromDegrees(Number(lng), Number(lat), 0);
  viewer.camera.flyToBoundingSphere(new Cesium.BoundingSphere(target, 40), {
    offset: new Cesium.HeadingPitchRange(
      0,
      Cesium.Math.toRadians(-45),
      height
    ),
    duration: 1.6,
  });
}
